/**
 * Country corridor data — basmati export from India to N markets.
 *
 * Each entry powers one /basmati-rice/export-from-india-to-<slug> route.
 * Content is real per-country regulatory data — port, regulator, halal
 * body, labelling rules, trade-agreement status, currency, lead time,
 * preferred basmati variant. Facts (regulator names, port names, ISO
 * codes, trade agreements) are public-record information, not
 * copyrightable, and the prose framing is original.
 *
 * Sources for facts: APEDA exporter guidance, destination-country
 * customs portals, WTO Trade Policy Reviews, and our own shipping
 * experience.
 */

const SITE = "https://www.viloraimpex.com";
const ORG_REF = `${SITE}/#organization`;
const FOUNDER_REF = `${SITE}/founder/darshit-anadkat#person`;

export const CORRIDORS = {
  // ===== GCC / Gulf =====
  "uae": {
    slug: "uae",
    country: "United Arab Emirates",
    iso: "AE",
    flag: "🇦🇪",
    region: "GCC",
    loadingPort: "Mundra",
    arrivalPort: "Jebel Ali (Dubai), Khalifa (Abu Dhabi), Mina Zayed",
    transitDays: "7–9",
    currency: "AED (United Arab Emirates Dirham)",
    regulator: "MOIAT (Ministry of Industry & Advanced Technology) + Dubai Municipality FSD + Abu Dhabi ADAFSA",
    halalRequired: true,
    halalAcceptedBodies: "MOIAT-accredited Indian halal bodies: Halal India Pvt Ltd, Jamiat Ulama-i-Hind Halal Trust",
    labellingLanguages: "English + Arabic, bilingual mandatory for retail",
    tradeAgreement: "India–UAE CEPA (in force since May 2022)",
    dutyNote: "Preferential CEPA duty for basmati when supplied with Form CEPA certificate of origin. Always confirm current rate with UAE customs broker.",
    preferredVariety: "1121 Sella (creamy + golden), 1121 Steam, increasingly 1718 Sella for supply-resilience",
    typicalMOQ: "20ft FCL (~25 MT)",
    leadTimePIToWarehouse: "18–26 days (10–14 production + 7–9 transit + 1–3 clearance)",
    paymentTerms: "30% advance + 70% against BL scan, or irrevocable L/C at sight",
    docExtras: "Preferential COO (Form CEPA), MOIAT halal certificate, SFDA-aligned nutritional panel for retail SKUs",
    marketNotes: "Largest single Indian basmati destination globally. Both Dubai retail (deep-golden 1121 Sella dominant) and Abu Dhabi government feeding programs. Free-zone re-export business through Jebel Ali significant for African and Iranian onward distribution.",
  },
  "saudi-arabia": {
    slug: "saudi-arabia",
    country: "Saudi Arabia",
    iso: "SA",
    flag: "🇸🇦",
    region: "GCC",
    loadingPort: "Mundra",
    arrivalPort: "Dammam (King Abdulaziz Port), Jeddah",
    transitDays: "9–12",
    currency: "SAR (Saudi Riyal)",
    regulator: "SFDA (Saudi Food and Drug Authority)",
    halalRequired: true,
    halalAcceptedBodies: "SFDA-recognised list: Halal India Pvt Ltd, Jamiat Ulama-i-Hind, Halal Council of India (confirm with importer's SFDA registration)",
    labellingLanguages: "Bilingual English + Arabic, DD/MM/YYYY date format, SFDA-format nutritional panel",
    tradeAgreement: "No bilateral FTA. GCC import duty 5% (rice).",
    dutyNote: "5% GCC unified customs duty. 15% VAT at import (importer-side).",
    preferredVariety: "1121 Golden Sella (dominant), 1121 Sella, growing 1718 Golden Sella for supply diversification",
    typicalMOQ: "20ft FCL (~25 MT); 40ft FCL common for repeat distributors",
    leadTimePIToWarehouse: "22–32 days (production + 9–12 sea + 2–4 clearance + 1–2 inland)",
    paymentTerms: "L/C at sight (most common), or 100% advance for first shipment to new importer",
    docExtras: "SFDA product registration certificate (held by importer), Saudi MOC trade license copy, halal cert from SFDA-accepted body, SASO-compliant labelling",
    marketNotes: "Among top three Indian basmati destinations globally. Saudi consumers strongly prefer deep-golden Sella; per-capita basmati consumption among the world's highest. Ramadan and Hajj-season demand spikes drive Q1-Q2 procurement. SFDA registration for retail SKUs requires 4–8 weeks lead time — plan before first shipment.",
  },
  "qatar": {
    slug: "qatar",
    country: "Qatar",
    iso: "QA",
    flag: "🇶🇦",
    region: "GCC",
    loadingPort: "Mundra",
    arrivalPort: "Hamad Port (Doha)",
    transitDays: "9–11",
    currency: "QAR (Qatari Riyal)",
    regulator: "MoPH Qatar Food Safety Department + GSO standards alignment",
    halalRequired: true,
    halalAcceptedBodies: "GSO + Indian halal bodies recognised in GCC (MOIAT-accredited list)",
    labellingLanguages: "Bilingual English + Arabic, GSO-compliant",
    tradeAgreement: "No bilateral FTA. GCC 5% customs duty.",
    dutyNote: "5% GCC unified customs. No VAT.",
    preferredVariety: "1121 Sella (light-golden to deep-golden), 1121 Steam for premium retail",
    typicalMOQ: "20ft FCL (~25 MT)",
    leadTimePIToWarehouse: "20–28 days",
    paymentTerms: "L/C at sight or 30% advance + 70% against BL",
    docExtras: "GSO-compliant labelling, halal cert, COO",
    marketNotes: "Premium-tier market — Qatari retail skews toward expensive aged 1121 Sella, with private-label gift packs around major holidays. Hamad Port clearance is fast for clean documentation (1–2 days).",
  },
  "oman": {
    slug: "oman",
    country: "Oman",
    iso: "OM",
    flag: "🇴🇲",
    region: "GCC",
    loadingPort: "Mundra / Kandla",
    arrivalPort: "Sohar Port, Salalah Port, Sultan Qaboos (Muscat)",
    transitDays: "8–11 (Salalah closer than Sohar from Mundra)",
    currency: "OMR (Omani Rial)",
    regulator: "MOCIIP / Ministry of Commerce + Oman Food Safety (CRA-aligned with GSO)",
    halalRequired: true,
    halalAcceptedBodies: "GCC GSO-accredited halal bodies",
    labellingLanguages: "Bilingual English + Arabic",
    tradeAgreement: "No bilateral FTA. GCC 5% customs.",
    dutyNote: "5% GCC unified customs.",
    preferredVariety: "1121 Sella, 1121 Steam, some 1509 for value retail",
    typicalMOQ: "20ft FCL (~25 MT)",
    leadTimePIToWarehouse: "20–28 days",
    paymentTerms: "L/C at sight, or 30% advance + 70% against BL",
    docExtras: "Halal cert, COO, GSO-compliant labelling",
    marketNotes: "Salalah is also a major transhipment hub for East African onward trade. Sohar is rising as Oman's principal container port. Oman's middle-class basmati demand is steady year-round.",
  },
  "kuwait": {
    slug: "kuwait",
    country: "Kuwait",
    iso: "KW",
    flag: "🇰🇼",
    region: "GCC",
    loadingPort: "Mundra / Kandla",
    arrivalPort: "Shuwaikh, Shuaiba (Kuwait City)",
    transitDays: "10–12",
    currency: "KWD (Kuwaiti Dinar)",
    regulator: "PAFN (Public Authority for Food and Nutrition) + GSO standards",
    halalRequired: true,
    halalAcceptedBodies: "GCC GSO-accredited halal bodies",
    labellingLanguages: "Bilingual English + Arabic",
    tradeAgreement: "No bilateral FTA. GCC 5% customs.",
    dutyNote: "5% GCC unified customs.",
    preferredVariety: "1121 Sella, 1121 Steam, 1121 Golden Sella for premium retail",
    typicalMOQ: "20ft FCL (~25 MT)",
    leadTimePIToWarehouse: "22–30 days",
    paymentTerms: "L/C at sight, or 100% advance for new buyer first shipment",
    docExtras: "PAFN registration for retail SKUs (importer-side), halal cert, COO",
    marketNotes: "Premium consumer market; PAFN compliance + halal cert + bilingual labelling required for retail. Bulk distribution and HoReCa also significant.",
  },
  "bahrain": {
    slug: "bahrain",
    country: "Bahrain",
    iso: "BH",
    flag: "🇧🇭",
    region: "GCC",
    loadingPort: "Mundra",
    arrivalPort: "Khalifa Bin Salman Port (KBSP)",
    transitDays: "10–12",
    currency: "BHD (Bahraini Dinar)",
    regulator: "NHRA + Ministry of Industry & Commerce + GSO standards",
    halalRequired: true,
    halalAcceptedBodies: "GCC GSO-accredited halal bodies",
    labellingLanguages: "Bilingual English + Arabic",
    tradeAgreement: "No bilateral FTA. GCC 5% customs.",
    dutyNote: "5% GCC customs.",
    preferredVariety: "1121 Sella, 1121 Steam, 1509 Sella for value retail",
    typicalMOQ: "20ft FCL or LCL trial shipments accepted",
    leadTimePIToWarehouse: "22–30 days",
    paymentTerms: "L/C at sight or 30% advance + 70% against BL",
    docExtras: "Halal cert, COO, GSO-compliant labelling",
    marketNotes: "Smaller per-capita market than UAE/Saudi but high-margin premium retail. KBSP is well-connected to GCC inland onward distribution.",
  },

  // ===== Levant / Middle East =====
  "iraq": {
    slug: "iraq",
    country: "Iraq",
    iso: "IQ",
    flag: "🇮🇶",
    region: "Middle East",
    loadingPort: "Mundra / Kandla",
    arrivalPort: "Umm Qasr (Basra), Khor Al-Zubair",
    transitDays: "10–14",
    currency: "IQD (Iraqi Dinar) / USD for trade",
    regulator: "Ministry of Trade + Central Organization for Standardization & Quality Control (COSQC)",
    halalRequired: true,
    halalAcceptedBodies: "Halal cert from any Indian body accepted; COSQC validation at destination",
    labellingLanguages: "Arabic primary, English secondary acceptable",
    tradeAgreement: "No bilateral FTA. Standard MFN customs apply.",
    dutyNote: "Standard customs duty (verify with importer's broker — Iraqi duty varies by tariff line).",
    preferredVariety: "1121 Raw (Iranian-cuisine-adjacent timman traditions), 1121 Sella, bulk 1509",
    typicalMOQ: "20ft FCL or 40ft FCL (large-volume distribution)",
    leadTimePIToWarehouse: "24–34 days",
    paymentTerms: "L/C at sight from major bank; some 100% advance to Iraqi importer accounts",
    docExtras: "COSQC validation at destination, COO, halal cert, phytosanitary",
    marketNotes: "Large-volume non-retail and food-aid market. Traditional Iraqi cuisine (timman, biryani-adjacent dishes) uses long-grain basmati extensively. Multiple importer channels through Basra ports.",
  },
  "jordan": {
    slug: "jordan",
    country: "Jordan",
    iso: "JO",
    flag: "🇯🇴",
    region: "Levant",
    loadingPort: "Mundra",
    arrivalPort: "Aqaba",
    transitDays: "14–16",
    currency: "JOD (Jordanian Dinar)",
    regulator: "Jordan Food and Drug Administration (JFDA)",
    halalRequired: true,
    halalAcceptedBodies: "JFDA-accepted halal bodies (Indian Halal India Pvt Ltd commonly accepted)",
    labellingLanguages: "Arabic primary, English secondary",
    tradeAgreement: "No bilateral FTA.",
    dutyNote: "Standard customs duty.",
    preferredVariety: "1121 Sella (creamy and golden), 1121 Steam for retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "26–34 days",
    paymentTerms: "L/C at sight",
    docExtras: "JFDA registration for retail SKUs, halal cert, COO",
    marketNotes: "Aqaba is Jordan's only sea port; reasonably fast clearance. Refugee-population food-aid and HoReCa biryani-chain supply are both meaningful segments.",
  },
  "lebanon": {
    slug: "lebanon",
    country: "Lebanon",
    iso: "LB",
    flag: "🇱🇧",
    region: "Levant",
    loadingPort: "Mundra",
    arrivalPort: "Beirut Port, Tripoli Port",
    transitDays: "14–18",
    currency: "LBP (Lebanese Pound) / USD for trade",
    regulator: "Ministry of Economy & Trade + LIBNOR standards",
    halalRequired: true,
    halalAcceptedBodies: "Standard Indian halal bodies accepted",
    labellingLanguages: "Arabic + English acceptable; some retail prefers French + Arabic",
    tradeAgreement: "No bilateral FTA.",
    dutyNote: "Standard customs duty.",
    preferredVariety: "1121 Sella, 1121 Steam, some 1121 Raw for diaspora cuisine",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "28–36 days",
    paymentTerms: "L/C at sight from major bank; advance payments common given Lebanon's currency situation",
    docExtras: "LIBNOR-compliant labelling, halal cert, COO",
    marketNotes: "Currency volatility means most trade settles in USD. Beirut port handles majority of consumer-goods import volume; Tripoli is secondary. Lebanese diaspora cuisine drives premium basmati demand.",
  },
  "egypt": {
    slug: "egypt",
    country: "Egypt",
    iso: "EG",
    flag: "🇪🇬",
    region: "North Africa / Middle East",
    loadingPort: "Mundra",
    arrivalPort: "Alexandria, Port Said, Damietta",
    transitDays: "12–16",
    currency: "EGP (Egyptian Pound) / USD for trade",
    regulator: "GOEIC (General Organization for Export & Import Control) + Egyptian Standards (NIS)",
    halalRequired: true,
    halalAcceptedBodies: "GOEIC-accepted Indian bodies (Halal India Pvt Ltd among them)",
    labellingLanguages: "Arabic mandatory",
    tradeAgreement: "No bilateral FTA. Egypt has its own rice industry; basmati is import-only for premium retail.",
    dutyNote: "Tariff varies — basmati typically faces moderate duty as a non-domestic-produced specialty rice. Confirm with Egyptian customs broker.",
    preferredVariety: "1121 Sella (golden + creamy), 1509 Sella for value retail",
    typicalMOQ: "20ft FCL or 40ft FCL",
    leadTimePIToWarehouse: "26–34 days",
    paymentTerms: "L/C at sight (USD-denominated)",
    docExtras: "GOEIC pre-shipment inspection, Arabic labelling, halal cert, COO",
    marketNotes: "Egypt is a domestic-rice-producing nation, so basmati imports serve the premium-retail and HoReCa segment specifically. Alexandria is the dominant gateway. GOEIC pre-shipment inspection requirement adds 5–7 days to lead time.",
  },
  "yemen": {
    slug: "yemen",
    country: "Yemen",
    iso: "YE",
    flag: "🇾🇪",
    region: "Middle East",
    loadingPort: "Mundra",
    arrivalPort: "Aden, Hodeidah, Mukalla",
    transitDays: "10–14",
    currency: "YER (Yemeni Rial) / USD for trade",
    regulator: "Yemen Standardization, Metrology & Quality Control Organization (YSMO)",
    halalRequired: true,
    halalAcceptedBodies: "Standard Indian halal bodies accepted",
    labellingLanguages: "Arabic primary",
    tradeAgreement: "No bilateral FTA. Yemen import regime affected by conflict zones; route-planning matters.",
    dutyNote: "Standard customs duty; route reliability varies by port.",
    preferredVariety: "1121 Sella, value-tier 1509 Sella for food-aid pipelines",
    typicalMOQ: "20ft FCL or 40ft FCL (food-aid volumes)",
    leadTimePIToWarehouse: "24–34 days; longer when re-routing through Djibouti for Aden",
    paymentTerms: "L/C at sight from major bank; food-aid programs pay against UN/WFP receipts",
    docExtras: "COO, halal cert, YSMO-compliant labelling, phytosanitary",
    marketNotes: "Significant food-aid and basic-supply channel. UN WFP and other humanitarian programs procure bulk basmati and IR64 for Yemen distribution. Route-planning consideration: Aden is functional; Hodeidah subject to access constraints depending on the period.",
  },
  "israel": {
    slug: "israel",
    country: "Israel",
    iso: "IL",
    flag: "🇮🇱",
    region: "Middle East",
    loadingPort: "Mundra",
    arrivalPort: "Haifa Port, Ashdod Port",
    transitDays: "12–16",
    currency: "ILS (Israeli Shekel)",
    regulator: "Israeli Ministry of Health (food import licensing) + Israel Standards Institute",
    halalRequired: false,
    halalAcceptedBodies: "Halal not standard; Kosher certification can be relevant for specific retail channels.",
    labellingLanguages: "Hebrew mandatory, English + Arabic as secondary",
    tradeAgreement: "No bilateral FTA with India.",
    dutyNote: "Standard customs duty for basmati.",
    preferredVariety: "1121 Sella (golden + creamy), 1121 Steam, Israeli food-service occasionally specifies organic-certified rice",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "26–34 days",
    paymentTerms: "L/C at sight from major Israeli bank, or 30% advance + 70% against BL",
    docExtras: "Hebrew labelling, Ministry of Health food import licence, COO. Kosher certification (Badatz, OU, Rabbinate) for specific retail channels.",
    marketNotes: "Distinct from Gulf demand — kosher-certified basmati commands a premium in religious retail segments. Israeli HoReCa biryani houses are an emerging mid-volume channel. Haifa is the primary gateway.",
  },
  "cyprus": {
    slug: "cyprus",
    country: "Cyprus",
    iso: "CY",
    flag: "🇨🇾",
    region: "EU / Mediterranean",
    loadingPort: "Mundra",
    arrivalPort: "Limassol",
    transitDays: "16–20",
    currency: "EUR",
    regulator: "Cyprus Food Safety Authority + EU regulations (Cyprus is in EU)",
    halalRequired: false,
    halalAcceptedBodies: "Halal not mandatory; relevant for specific retail channels.",
    labellingLanguages: "Greek primary, English secondary, EU-format nutritional panel",
    tradeAgreement: "EU import regime (no India–EU FTA; standard MFN tariff with EU GSP+ on some Indian goods)",
    dutyNote: "EU MFN tariff for rice; basmati has dedicated CN codes (1006.30.27, .48, .67, .98).",
    preferredVariety: "1121 Steam, 1121 Sella, premium retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "28–36 days",
    paymentTerms: "L/C at sight or 30% advance + 70% against BL",
    docExtras: "EU 2073/2005 microbial compliance, pesticide-residue MRL testing (especially tricyclazole, chlorpyrifos), EU-format labelling, COO, halal cert if for halal retail",
    marketNotes: "Smaller market but high-margin. Cyprus's geography makes it a tourism-driven HoReCa basmati buyer. EU regulatory compliance applies fully.",
  },
  "turkey": {
    slug: "turkey",
    country: "Turkey",
    iso: "TR",
    flag: "🇹🇷",
    region: "Middle East / Europe",
    loadingPort: "Mundra",
    arrivalPort: "Mersin, Istanbul (Ambarli), Izmir",
    transitDays: "15–20",
    currency: "TRY (Turkish Lira) / USD for trade",
    regulator: "Turkish Ministry of Agriculture & Forestry + Turkish Standards Institution (TSE)",
    halalRequired: true,
    halalAcceptedBodies: "Halal certificate from Turkish HAK or accepted Indian bodies",
    labellingLanguages: "Turkish mandatory",
    tradeAgreement: "No bilateral FTA. Turkey applies MFN tariff with own preferential agreements (Customs Union with EU).",
    dutyNote: "Standard tariff for basmati; verify with Turkish broker.",
    preferredVariety: "1121 Sella, 1121 Steam for premium retail and HoReCa",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "28–36 days",
    paymentTerms: "L/C at sight from Turkish bank; advance payments common",
    docExtras: "TSE-compliant labelling, halal cert, COO, food-import licensing per Ministry of Agriculture",
    marketNotes: "Turkey is also a major regional re-export hub via Mersin for Central Asia. Domestic Turkish rice is short-grain; basmati imports serve specialty retail and HoReCa.",
  },
  "georgia": {
    slug: "georgia",
    country: "Georgia",
    iso: "GE",
    flag: "🇬🇪",
    region: "Eurasia",
    loadingPort: "Mundra",
    arrivalPort: "Poti, Batumi",
    transitDays: "20–26 (often via Black Sea trans-shipment)",
    currency: "GEL (Georgian Lari) / USD",
    regulator: "National Food Agency of Georgia + technical regulations aligned with EU",
    halalRequired: false,
    halalAcceptedBodies: "Halal not standard; relevant only for specific retail channels.",
    labellingLanguages: "Georgian primary, English secondary",
    tradeAgreement: "Georgia–EU DCFTA aligns Georgian standards with EU; no bilateral India FTA.",
    dutyNote: "Low MFN tariff (Georgia maintains liberalised trade regime). Verify with Georgian broker.",
    preferredVariety: "1121 Sella, 1121 Steam for premium retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "32–42 days (longer due to Black Sea trans-shipment)",
    paymentTerms: "L/C at sight, USD-denominated",
    docExtras: "Georgian-language labelling, COO",
    marketNotes: "Georgia is a developing market for premium basmati — niche retail and HoReCa. Often onward-distributes to Caucasus countries (Armenia, Azerbaijan). Trans-shipment through Turkish or Greek ports adds complexity to logistics.",
  },

  // ===== Europe (EU + UK) =====
  "germany": {
    slug: "germany-europe",
    country: "Germany",
    iso: "DE",
    flag: "🇩🇪",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Hamburg, Bremerhaven",
    transitDays: "22–28",
    currency: "EUR",
    regulator: "BVL + BfR + state-level food safety authorities; full EU regulatory regime applies",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for Turkish/Arab diaspora retail channels (Halal India Pvt Ltd accepted by major retailers).",
    labellingLanguages: "German mandatory, EU-format nutritional panel",
    tradeAgreement: "EU MFN tariff. EU 2073/2005 microbial limits and EU MRL regulations apply.",
    dutyNote: "Basmati CN codes 1006.30.27 / .48 / .67 / .98. EU MFN tariff applies (varies by processing form).",
    preferredVariety: "1121 Steam (premium retail), 1121 Sella, 1509 Steam (HoReCa volume), increasingly organic-certified basmati",
    typicalMOQ: "20ft FCL or 40ft FCL",
    leadTimePIToWarehouse: "34–44 days (production + 22–28 transit + 1–3 clearance)",
    paymentTerms: "L/C at sight from major German bank; established buyers move to 30% advance + 70% against BL",
    docExtras: "EU 2073/2005 microbial certificate, pesticide-residue MRL report (tricyclazole limit 0.01 mg/kg critical), aflatoxin spec, German labelling, EU CN-code-correct invoicing, COO, halal cert for halal-retail SKUs",
    marketNotes: "Largest EU basmati import market. Tricyclazole MRL is THE compliance hurdle for Indian basmati into Germany — lots that fail this test get rejected and create a paper trail. Steam-sterilized organic-certified basmati commands a 30–50% premium. Hamburg is the dominant gateway. Turkish-German and Indian-German diaspora drive premium retail demand; mainstream German retail also stocks basmati at increasing volumes.",
  },
  "france": {
    slug: "france-europe",
    country: "France",
    iso: "FR",
    flag: "🇫🇷",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Le Havre, Marseille-Fos",
    transitDays: "22–28",
    currency: "EUR",
    regulator: "DGCCRF + DGAL (food safety); full EU regulatory regime applies",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for North African and Middle Eastern diaspora retail (AVS, Mosquée de Paris, or Indian-body certificates accepted depending on retailer).",
    labellingLanguages: "French mandatory, EU-format nutritional panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "Basmati CN codes apply; EU MFN tariff.",
    preferredVariety: "1121 Steam, 1121 Sella, increasingly organic-certified varieties for biological retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "34–44 days",
    paymentTerms: "L/C at sight, or 30% advance + 70% against BL",
    docExtras: "EU 2073/2005 microbial, tricyclazole MRL (0.01 mg/kg), aflatoxin, French labelling, COO, halal cert for halal-retail SKUs",
    marketNotes: "Large North African (Maghreb) and South Asian diaspora drive basmati demand. Marseille-Fos and Le Havre both handle volume; Marseille is closer for southern France distribution. Mainstream supermarket chains (Carrefour, Casino, Auchan) increasingly stock basmati as part of world-foods range.",
  },
  "uk": {
    slug: "united-kingdom",
    country: "United Kingdom",
    iso: "GB",
    flag: "🇬🇧",
    region: "Europe (non-EU)",
    loadingPort: "Mundra",
    arrivalPort: "Felixstowe, London Gateway, Southampton",
    transitDays: "22–28",
    currency: "GBP (Pound Sterling)",
    regulator: "FSA (Food Standards Agency), DEFRA, Trading Standards (post-Brexit, UK has its own regulatory regime)",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for South Asian diaspora retail (HMC, HFA, or Indian-body certificates).",
    labellingLanguages: "English",
    tradeAgreement: "India–UK FTA in negotiation as of 2025; bilateral framework signed. No active preferential tariff for basmati at present.",
    dutyNote: "Post-Brexit UK Global Tariff applies; basmati duty similar to EU MFN historically.",
    preferredVariety: "1121 Steam, 1121 Raw (Indian diaspora retail preference for white), 1121 Sella, 1509 Steam, premium aged",
    typicalMOQ: "20ft FCL or 40ft FCL",
    leadTimePIToWarehouse: "34–44 days",
    paymentTerms: "L/C at sight, or 30% advance + 70% against BL",
    docExtras: "UK-specific labelling, FSA microbial spec, pesticide-residue MRL report, COO, halal cert (HMC commonly accepted for retail)",
    marketNotes: "Second-largest basmati market in Europe. Indian diaspora is a major consumer base — UK retail (Tesco, Sainsbury's, ASDA, Morrisons, Indian-owned chains like East End Foods) all stock basmati. UK consumers often prefer 1121 Raw over Sella (white over golden), distinct from Gulf preference. Brexit has added UK-specific labelling compliance.",
  },
  "netherlands": {
    slug: "netherlands-europe",
    country: "Netherlands",
    iso: "NL",
    flag: "🇳🇱",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Rotterdam",
    transitDays: "22–28",
    currency: "EUR",
    regulator: "NVWA (Netherlands Food and Consumer Product Safety Authority); full EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for Turkish/Moroccan diaspora retail (HFFIA, or Indian bodies accepted by retailer).",
    labellingLanguages: "Dutch mandatory, EU-format nutritional panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "EU MFN basmati tariff.",
    preferredVariety: "1121 Steam, 1121 Sella for diaspora retail; Rotterdam re-export onward to inland Europe",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "34–44 days",
    paymentTerms: "L/C at sight, or 30% advance + 70% against BL",
    docExtras: "EU 2073/2005, tricyclazole MRL, aflatoxin, Dutch labelling, COO",
    marketNotes: "Rotterdam is Europe's largest container port and a major basmati re-export hub. Dutch wholesalers onward-distribute basmati across Germany, Belgium, France, Scandinavia. Significant Surinamese-Indian and Moroccan diaspora retail.",
  },
  "italy": {
    slug: "italy-europe",
    country: "Italy",
    iso: "IT",
    flag: "🇮🇹",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Genoa, La Spezia, Trieste",
    transitDays: "22–28",
    currency: "EUR",
    regulator: "Ministero della Salute + ICQRF; full EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal relevant for North African diaspora retail.",
    labellingLanguages: "Italian mandatory, EU-format nutritional panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "Italy is a domestic rice-producing country (Arborio, Carnaroli, Vialone Nano) — basmati imports serve the specialty retail segment.",
    preferredVariety: "1121 Steam, 1121 Sella for premium retail and Indian-restaurant supply",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "34–44 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, Italian labelling, COO",
    marketNotes: "Italian basmati demand is specialty-only — domestic short-grain dominates the broader rice market. Indian restaurants in Milan, Rome, Florence drive basmati consumption. Premium retail (esselunga, Coop) carries basmati at the world-foods aisle.",
  },
  "spain": {
    slug: "spain-europe",
    country: "Spain",
    iso: "ES",
    flag: "🇪🇸",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Valencia, Algeciras, Barcelona",
    transitDays: "20–26 (Algeciras shorter, Valencia comparable)",
    currency: "EUR",
    regulator: "AESAN + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal relevant for North African diaspora retail.",
    labellingLanguages: "Spanish mandatory, regional language (Catalan, Galician, Basque) for some retail",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "Spain has domestic rice production (Calasparra, Bomba for paella) — basmati is specialty import.",
    preferredVariety: "1121 Steam, 1121 Sella for retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "32–42 days",
    paymentTerms: "L/C at sight or 30% advance + 70% against BL",
    docExtras: "EU 2073/2005, tricyclazole MRL, Spanish labelling, COO",
    marketNotes: "Algeciras is a major trans-shipment hub. Spanish supermarket chains (Mercadona, El Corte Inglés, Carrefour Spain) carry basmati for specialty cuisine. South-Asian diaspora is smaller than in northern Europe.",
  },
  "belgium": {
    slug: "belgium-europe",
    country: "Belgium",
    iso: "BE",
    flag: "🇧🇪",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Antwerp, Zeebrugge",
    transitDays: "22–28",
    currency: "EUR",
    regulator: "FASFC + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal relevant for Moroccan and Turkish diaspora retail.",
    labellingLanguages: "Dutch + French + German (Belgium's three official languages)",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "EU MFN basmati tariff.",
    preferredVariety: "1121 Steam, 1121 Sella for Indian and diaspora retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "34–44 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, trilingual labelling (NL/FR/DE), COO",
    marketNotes: "Antwerp is a major re-export hub for inland Europe. Belgian diaspora retail and HoReCa biryani-chain demand drive volume. Trilingual labelling requirement is the operational nuance.",
  },
  "denmark": {
    slug: "denmark-europe",
    country: "Denmark",
    iso: "DK",
    flag: "🇩🇰",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Copenhagen, Aarhus",
    transitDays: "24–30",
    currency: "DKK (Danish Krone)",
    regulator: "Fødevarestyrelsen (Danish Veterinary and Food Administration) + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for Turkish and Middle Eastern diaspora retail.",
    labellingLanguages: "Danish mandatory, EU-format panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "EU MFN basmati tariff.",
    preferredVariety: "1121 Steam, 1121 Sella for specialty retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "36–46 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, Danish labelling, COO, halal cert if halal-retail",
    marketNotes: "Smaller market driven by South Asian and Middle Eastern diaspora retail. Aarhus port handles inland Scandinavian onward distribution.",
  },
  "sweden": {
    slug: "sweden-europe",
    country: "Sweden",
    iso: "SE",
    flag: "🇸🇪",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Gothenburg, Stockholm",
    transitDays: "26–32",
    currency: "SEK (Swedish Krona)",
    regulator: "Livsmedelsverket (Swedish Food Agency) + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for Middle Eastern diaspora retail.",
    labellingLanguages: "Swedish mandatory, EU-format panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "EU MFN basmati tariff.",
    preferredVariety: "1121 Steam, 1121 Sella, specialty organic-certified retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "38–48 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, Swedish labelling, COO",
    marketNotes: "Middle Eastern refugee-origin community drives a significant basmati segment. Gothenburg port is the main gateway. Organic-certified basmati shows strong growth in Swedish premium retail.",
  },
  "norway": {
    slug: "norway-europe",
    country: "Norway",
    iso: "NO",
    flag: "🇳🇴",
    region: "Europe (EEA, non-EU)",
    loadingPort: "Mundra",
    arrivalPort: "Oslo",
    transitDays: "28–34",
    currency: "NOK (Norwegian Krone)",
    regulator: "Mattilsynet (Norwegian Food Safety Authority); EEA-aligned with EU regulations",
    halalRequired: false,
    halalAcceptedBodies: "Halal relevant for Middle Eastern diaspora.",
    labellingLanguages: "Norwegian (Bokmål) mandatory",
    tradeAgreement: "EFTA / EEA member; basmati tariff under EFTA preferential schemes.",
    dutyNote: "Norway maintains relatively liberalised import tariffs for rice as a non-domestic-production commodity.",
    preferredVariety: "1121 Steam, 1121 Sella",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "40–50 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU-aligned EU 2073/2005, tricyclazole MRL, Norwegian labelling, COO",
    marketNotes: "Smaller market driven by diaspora retail. Oslo is the main gateway. Premium retail and HoReCa biryani-house demand.",
  },
  "finland": {
    slug: "finland-europe",
    country: "Finland",
    iso: "FI",
    flag: "🇫🇮",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Helsinki",
    transitDays: "28–34",
    currency: "EUR",
    regulator: "Ruokavirasto (Finnish Food Authority) + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for Middle Eastern refugee-origin retail.",
    labellingLanguages: "Finnish + Swedish (bilingual mandatory), EU-format panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "EU MFN basmati tariff.",
    preferredVariety: "1121 Steam, 1121 Sella for specialty retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "40–50 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, bilingual Finnish/Swedish labelling, COO",
    marketNotes: "Smaller market. Finnish-Swedish bilingual labelling is an operational requirement. Demand driven by diaspora retail and HoReCa specialty.",
  },
  "portugal": {
    slug: "portugal-europe",
    country: "Portugal",
    iso: "PT",
    flag: "🇵🇹",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Sines, Lisbon",
    transitDays: "20–26",
    currency: "EUR",
    regulator: "ASAE + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for African-Portuguese diaspora.",
    labellingLanguages: "Portuguese mandatory, EU-format panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "Portugal has its own short-grain rice production; basmati is specialty import.",
    preferredVariety: "1121 Steam, 1121 Sella for Indian-Portuguese and African-Portuguese retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "32–42 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, Portuguese labelling, COO",
    marketNotes: "Smaller specialty market. Indian-Portuguese (Goa heritage) and African-Portuguese (Angola, Mozambique heritage) communities drive demand.",
  },
  "poland": {
    slug: "poland-europe",
    country: "Poland",
    iso: "PL",
    flag: "🇵🇱",
    region: "EU",
    loadingPort: "Mundra",
    arrivalPort: "Gdańsk, Gdynia (often via re-shipment from Hamburg)",
    transitDays: "26–32",
    currency: "PLN (Polish Złoty) / EUR for trade",
    regulator: "GIS + IJHARS + EU regulatory regime",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for niche Middle Eastern diaspora retail.",
    labellingLanguages: "Polish mandatory, EU-format panel",
    tradeAgreement: "EU MFN tariff regime.",
    dutyNote: "EU MFN basmati tariff.",
    preferredVariety: "1121 Steam, 1121 Sella for specialty retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "36–46 days",
    paymentTerms: "L/C at sight",
    docExtras: "EU 2073/2005, tricyclazole MRL, Polish labelling, COO",
    marketNotes: "Polish basmati consumption is rising — specialty Asian-cuisine retail and HoReCa Indian restaurants. Gdańsk port is the dominant gateway for Central European trans-shipment.",
  },

  // ===== North America =====
  "usa": {
    slug: "usa",
    country: "United States of America",
    iso: "US",
    flag: "🇺🇸",
    region: "North America",
    loadingPort: "Mundra",
    arrivalPort: "Los Angeles, Long Beach, Oakland (West Coast); New York/New Jersey, Savannah, Norfolk (East Coast)",
    transitDays: "24–32 (West Coast), 28–35 (East Coast)",
    currency: "USD",
    regulator: "FDA (Food and Drug Administration), USDA, CBP (Customs and Border Protection)",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for South Asian and Middle Eastern diaspora retail (IFANCA, HMS commonly accepted).",
    labellingLanguages: "English; some markets benefit from Spanish secondary labelling",
    tradeAgreement: "No bilateral FTA. USA has a per-country tariff regime for basmati.",
    dutyNote: "US tariff for basmati imports — verify with US customs broker against current schedules.",
    preferredVariety: "1121 Raw (Indian-diaspora retail standard, white preferred over golden), 1121 Steam, premium aged retail",
    typicalMOQ: "40ft FCL common for repeat distributors",
    leadTimePIToWarehouse: "36–46 days (West Coast) / 40–50 days (East Coast)",
    paymentTerms: "L/C at sight from major US bank, or 30% advance + 70% against BL",
    docExtras: "FDA FSMA (Food Safety Modernization Act) compliance, FDA prior notice for food shipments, US Customs entry, English labelling per US FDA, COO, aflatoxin compliance",
    marketNotes: "Large Indian-American diaspora drives basmati demand. Indian-American retail chains (Patel Brothers, Apna Bazaar, Subzi Mandi, India Bazaar) are major buyers. Mainstream retail (Whole Foods, Costco, Sprouts) increasingly stocks specialty basmati. US consumers prefer white (Raw) over golden (Sella). FDA FSMA compliance is non-negotiable. Aflatoxin testing critical.",
  },
  "canada": {
    slug: "canada",
    country: "Canada",
    iso: "CA",
    flag: "🇨🇦",
    region: "North America",
    loadingPort: "Mundra",
    arrivalPort: "Vancouver, Montreal, Halifax",
    transitDays: "28–34 (Pacific via Vancouver) / 34–42 (Atlantic via Montreal/Halifax)",
    currency: "CAD (Canadian Dollar)",
    regulator: "CFIA (Canadian Food Inspection Agency), Health Canada",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for South Asian and Middle Eastern diaspora retail.",
    labellingLanguages: "Bilingual English + French (mandatory by federal law)",
    tradeAgreement: "No bilateral FTA. Canada applies own tariff schedule.",
    dutyNote: "Verify with Canadian customs broker against current basmati tariff.",
    preferredVariety: "1121 Raw, 1121 Steam, 1121 Sella for South Asian and Middle Eastern diaspora retail",
    typicalMOQ: "20ft FCL or 40ft FCL",
    leadTimePIToWarehouse: "38–48 days (Pacific) / 44–56 days (Atlantic)",
    paymentTerms: "L/C at sight, or 30% advance + 70% against BL",
    docExtras: "CFIA Safe Food for Canadians Regulations (SFCR) compliance, bilingual English/French labelling, COO, halal cert for halal-retail SKUs",
    marketNotes: "Large Indian-Canadian and Punjabi-Sikh diaspora drives basmati volume. Toronto and Vancouver are the dominant retail clusters. Bilingual labelling is a federal compliance requirement. Pacific transit shorter than Atlantic for Mundra origin.",
  },

  // ===== Asia-Pacific =====
  "singapore": {
    slug: "singapore",
    country: "Singapore",
    iso: "SG",
    flag: "🇸🇬",
    region: "Southeast Asia",
    loadingPort: "Mundra",
    arrivalPort: "PSA Singapore (Tuas, Pasir Panjang)",
    transitDays: "10–14",
    currency: "SGD (Singapore Dollar)",
    regulator: "Singapore Food Agency (SFA)",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert mandatory for Halal-Certified retail (MUIS Singapore halal, or recognised foreign bodies).",
    labellingLanguages: "English (primary); Chinese, Malay, Tamil as secondary depending on retail",
    tradeAgreement: "India–Singapore CECA (in force since 2005) provides preferential framework.",
    dutyNote: "Singapore is a free-trade hub; rice imports face minimal customs duty.",
    preferredVariety: "1121 Steam (white-grain preference), 1121 Sella, 1509 Steam, organic-certified basmati for premium retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "20–28 days",
    paymentTerms: "L/C at sight, USD-denominated",
    docExtras: "SFA-compliant labelling, halal cert for halal retail, COO under CECA for preferential treatment",
    marketNotes: "Singapore is a major trans-shipment hub for the region. Strong Indian and Malay diaspora drives retail basmati demand. NTUC FairPrice, Cold Storage and specialty Indian retail (Mustafa) all stock basmati at increasing volumes. Singapore consumers strongly prefer white (Steam or Raw) over golden (Sella).",
  },
  "malaysia": {
    slug: "malaysia",
    country: "Malaysia",
    iso: "MY",
    flag: "🇲🇾",
    region: "Southeast Asia",
    loadingPort: "Mundra",
    arrivalPort: "Port Klang, Penang, Pasir Gudang",
    transitDays: "12–16",
    currency: "MYR (Malaysian Ringgit)",
    regulator: "FSQD (Food Safety and Quality Division), KKM (Ministry of Health Malaysia)",
    halalRequired: true,
    halalAcceptedBodies: "JAKIM-recognised halal bodies. Indian: Halal India Pvt Ltd, Jamiat Ulama-i-Hind Halal Trust accepted by JAKIM.",
    labellingLanguages: "Bahasa Malaysia primary, English secondary",
    tradeAgreement: "India–Malaysia CECA + India–ASEAN FTA provide preferential framework.",
    dutyNote: "Preferential tariff under India–ASEAN FTA or India–Malaysia CECA.",
    preferredVariety: "1121 Steam, 1121 Sella for premium retail and HoReCa biryani chains",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "22–30 days",
    paymentTerms: "L/C at sight",
    docExtras: "JAKIM-recognised halal cert, FSQD-compliant Bahasa Malaysia labelling, COO under FTA, KKM food-import licence (importer-side)",
    marketNotes: "Mainstream Malaysian retail (Tesco, Giant, AEON) stocks basmati for the Indian-Malaysian, Mamak food-culture, and HoReCa biryani-chain segments. JAKIM halal certification is non-negotiable for retail. Port Klang is the dominant gateway.",
  },
  "indonesia": {
    slug: "indonesia",
    country: "Indonesia",
    iso: "ID",
    flag: "🇮🇩",
    region: "Southeast Asia",
    loadingPort: "Mundra",
    arrivalPort: "Tanjung Priok (Jakarta), Tanjung Perak (Surabaya), Belawan (Medan)",
    transitDays: "16–22",
    currency: "IDR (Indonesian Rupiah) / USD for trade",
    regulator: "BPOM (National Agency of Drug and Food Control) + MOA (Ministry of Agriculture quotas)",
    halalRequired: true,
    halalAcceptedBodies: "BPJPH-accredited halal bodies. Indian Halal India Pvt Ltd and Jamiat Ulama-i-Hind generally accepted via BPJPH-MUI framework.",
    labellingLanguages: "Bahasa Indonesia mandatory",
    tradeAgreement: "India–Indonesia bilateral framework + India–ASEAN FTA.",
    dutyNote: "Indonesia maintains rice-import quotas; basmati treated as specialty rice with separate import-permit framework. BULOG (state agency) holds key import licence; private importers operate under permit quotas.",
    preferredVariety: "1121 Steam, 1121 Sella for premium retail; specialty Indian-restaurant supply growing",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "28–38 days (import permit lead time can add 1–3 weeks)",
    paymentTerms: "L/C at sight",
    docExtras: "MOA import permit (importer-side, time-bound), BPJPH-MUI halal cert, BPOM food-import registration for retail SKUs, Bahasa labelling, COO",
    marketNotes: "Import permit (Persetujuan Impor) is a real operational hurdle — without it, no basmati clearance. Working with established Indonesian importers who already hold permits is the path of least resistance. Tanjung Priok handles majority of basmati import volume. Growing premium retail segment.",
  },
  "sri-lanka": {
    slug: "srilanka",
    country: "Sri Lanka",
    iso: "LK",
    flag: "🇱🇰",
    region: "South Asia",
    loadingPort: "Mundra or Chennai",
    arrivalPort: "Colombo, Hambantota",
    transitDays: "8–12 (Mundra) or 3–4 (Chennai)",
    currency: "LKR (Sri Lankan Rupee) / USD for trade",
    regulator: "Sri Lanka Standards Institution (SLSI) + Ministry of Health",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for Muslim-Sri-Lankan retail (HQAB or recognised foreign bodies).",
    labellingLanguages: "Sinhala + Tamil + English (trilingual common)",
    tradeAgreement: "India–Sri Lanka FTA provides preferential tariff framework.",
    dutyNote: "Preferential under ISFTA. Sri Lanka also has its own domestic rice; basmati is specialty import.",
    preferredVariety: "1121 Steam, 1121 Sella, 1509 Steam for Tamil-cuisine retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "18–26 days",
    paymentTerms: "L/C at sight, USD-denominated",
    docExtras: "ISFTA-form COO for preferential treatment, SLSI-compliant labelling, COO, halal cert for halal retail",
    marketNotes: "Colombo is the dominant gateway. Tamil-Sri-Lankan and Muslim-Sri-Lankan retail communities drive basmati demand. Sri Lanka's currency situation has volatility — USD-denominated L/C is the safe payment structure.",
  },
  "australia": {
    slug: "australia",
    country: "Australia",
    iso: "AU",
    flag: "🇦🇺",
    region: "Oceania",
    loadingPort: "Mundra",
    arrivalPort: "Sydney (Port Botany), Melbourne, Brisbane",
    transitDays: "20–26",
    currency: "AUD (Australian Dollar)",
    regulator: "DAFF + FSANZ (Food Standards Australia New Zealand)",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for diaspora retail (AFIC-recognised bodies).",
    labellingLanguages: "English; FSANZ-compliant nutritional panel",
    tradeAgreement: "India–Australia ECTA (Economic Cooperation and Trade Agreement) in force since Dec 2022.",
    dutyNote: "ECTA reduces tariffs on Indian basmati. Use Form ECTA COO to claim preferential treatment.",
    preferredVariety: "1121 Steam, 1121 Sella for Indian-Australian and broader Asian-diaspora retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "30–38 days",
    paymentTerms: "L/C at sight",
    docExtras: "ECTA Form COO for preferential duty, FSANZ-compliant labelling, DAFF biosecurity clearance (rice fumigation may be required), COO",
    marketNotes: "ECTA's preferential treatment makes Indian basmati cost-competitive in Australia. Indian-Australian diaspora (Sydney, Melbourne) is a major retail driver. Coles, Woolworths and Indian-owned chains all stock basmati. DAFF biosecurity is strict — fumigation certificates may be required for rice imports.",
  },
  "new-zealand": {
    slug: "new-zealand",
    country: "New Zealand",
    iso: "NZ",
    flag: "🇳🇿",
    region: "Oceania",
    loadingPort: "Mundra",
    arrivalPort: "Auckland (Ports of Auckland), Tauranga",
    transitDays: "24–30",
    currency: "NZD (New Zealand Dollar)",
    regulator: "MPI (Ministry for Primary Industries) + Food Standards Australia New Zealand (FSANZ)",
    halalRequired: false,
    halalAcceptedBodies: "Halal cert relevant for diaspora retail.",
    labellingLanguages: "English",
    tradeAgreement: "No bilateral FTA at present; trade discussions ongoing.",
    dutyNote: "Standard NZ tariff for basmati imports.",
    preferredVariety: "1121 Steam, 1121 Sella for Indian-New-Zealand and Pacific-Islander diaspora retail",
    typicalMOQ: "20ft FCL",
    leadTimePIToWarehouse: "34–42 days",
    paymentTerms: "L/C at sight",
    docExtras: "MPI biosecurity clearance (strict — fumigation likely), FSANZ-compliant labelling, COO",
    marketNotes: "Smaller market than Australia but growing. MPI biosecurity is among the strictest globally — anticipate fumigation requirements. Auckland is the dominant retail cluster for Indian-NZ diaspora.",
  },
};

export function buildCorridorJsonLd(slug) {
  const c = CORRIDORS[slug];
  if (!c) return null;
  const url = `${SITE}/basmati-rice/export-from-india-to-${slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        "url": url,
        "name": `Basmati Rice Export from India to ${c.country} | Vilora Impex`,
        "description": `Indian basmati rice export to ${c.country} — ${c.regulator} compliance, ${c.arrivalPort} port, ${c.transitDays} days transit, ${c.tradeAgreement}.`,
        "inLanguage": "en",
        "isPartOf": { "@id": `${SITE}/#website` },
        "about": { "@id": ORG_REF },
        "mentions": { "@id": FOUNDER_REF },
        "datePublished": "2026-05-22",
        "dateModified": "2026-05-22",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": [".corridor-h1", ".corridor-standfirst", ".corridor-section h2"] },
      },
      {
        "@type": "Service",
        "@id": `${url}#service`,
        "name": `Basmati Rice Export Service — India to ${c.country}`,
        "serviceType": "Basmati Rice Export",
        "provider": { "@id": ORG_REF },
        "areaServed": { "@type": "Country", "name": c.country, "addressCountry": c.iso },
        "audience": { "@type": "BusinessAudience", "audienceType": `Wholesale buyers, importers, distributors and HoReCa chains in ${c.country}` },
        "description": `End-to-end export of Indian basmati rice from Mundra/Kandla to ${c.arrivalPort}. ${c.regulator} compliance, ${c.preferredVariety} preferred varieties, ${c.paymentTerms}.`,
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
          { "@type": "ListItem", "position": 2, "name": "Basmati Rice", "item": `${SITE}/basmati-rice-exporter-from-india` },
          { "@type": "ListItem", "position": 3, "name": `Export to ${c.country}`, "item": url },
        ],
      },
      {
        "@type": "Product",
        "@id": `${url}#product`,
        "name": `Indian Basmati Rice for Export to ${c.country}`,
        "description": `Premium 1121, 1509, 1718 and Pusa basmati rice — exported from Mundra/Kandla, India to ${c.arrivalPort}, ${c.country}.`,
        "brand": { "@id": ORG_REF },
        "category": "Rice",
        "image": `${SITE}/hero-rice.jpg`,
        "offers": { "@id": `${SITE}/#agg-offer` },
      },
    ],
  };
}
